import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ExternalLink, ShieldAlert, RefreshCcw } from "lucide-react";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/lib/i18n";
import {
  peekCheckoutIntent,
  consumeCheckoutIntent,
  clearCheckoutIntent,
} from "@/lib/checkout_intent";
import { appendQueryParams } from "@/lib/url";

/**
 * Phase 7.UI.15m — post-auth checkout continuation page (hardened).
 *
 * Flow:
 *   1. Visitor clicks Pro/Premium on /pricing while logged out.
 *   2. We stash the intent (sessionStorage) and route through
 *      /signup?next=/billing/continue-checkout (or /login).
 *   3. After successful auth, the auth context redirects here.
 *   4. We WAIT until `loading === false` so the bearer/cookie is
 *      definitely attached before we call the protected endpoint.
 *   5. We PEEK the intent (do not consume) and call the authenticated
 *      checkout URL endpoint.
 *   6. ONLY on a valid checkout URL do we consume the intent and
 *      navigate to CopeCart.
 *   7. On failure (503 disabled, 400 invalid, 500 internal, network)
 *      we keep the intent in storage and show a Retry button so the
 *      user can try again without losing their plan selection.
 *
 * Diagnostics:
 *   We show the raw `error_code` + `debug` block from the backend ONLY
 *   to owners, admins, and developers (DEV build). Normal users see a
 *   localized, friendly message.
 */
export default function ContinueCheckout() {
  const { user, loading } = useAuth();
  const { t, locale } = useTranslation();
  const navigate = useNavigate();

  // Diagnostic state — `error` holds the structured backend payload
  // so the admin/owner panel can show `error_code` + `debug` verbatim.
  const [resolvedUrl, setResolvedUrl] = useState(null);
  const [error, setError] = useState(null); // { code, message, debug, http_status }
  const [intent, setIntent] = useState(null);
  const [busy, setBusy] = useState(false);

  // Compute the diagnostic gate ONCE per render. Owner == admin for v1.
  // DEV mode is also allowed so developers can see structured errors
  // without needing an admin email.
  const showDiagnostics = !!(
    user?.is_admin ||
    user?.is_owner ||
    user?.role === "admin" ||
    user?.role === "owner" ||
    (typeof process !== "undefined" && process.env && process.env.NODE_ENV === "development")
  );

  const attemptCheckout = useCallback(async () => {
    setBusy(true);
    setError(null);
    // Peek (do NOT consume yet) — we only consume after a verified
    // success so a transient backend error never burns the intent.
    const pending = peekCheckoutIntent();
    if (!pending) {
      navigate("/billing", { replace: true });
      return;
    }
    setIntent(pending);
    try {
      const params = new URLSearchParams({
        plan: pending.plan,
        interval: pending.interval,
        locale: pending.locale || locale || "en",
      });
      if (pending.affiliate_id) params.set("affiliate_id", pending.affiliate_id);
      const res = await api.get(`/billing/copecart/checkout-url?${params.toString()}`);
      const checkoutUrl = res?.data?.checkout_url;
      if (!checkoutUrl) {
        // Backend returned 200 but no URL — treat as a server-side bug
        // but keep the intent so the user can retry.
        setError({
          code: "no_checkout_url",
          message: t("billing.continue.error_generic"),
          debug: {
            note: "Server returned 200 but `checkout_url` was missing.",
            response_keys: res?.data ? Object.keys(res.data) : [],
          },
          http_status: res?.status || 200,
        });
        return;
      }
      // Defensive double-stamp of locale before redirect.
      const finalUrl = appendQueryParams(
        checkoutUrl,
        { locale: pending.locale || locale || "en" },
      );
      setResolvedUrl(finalUrl);
      // Success — NOW we can safely consume the intent so a refresh
      // doesn't open a second checkout session.
      consumeCheckoutIntent();
      // Auto-redirect immediately. The fallback button below covers
      // the rare case where the navigation is blocked.
      window.location.href = finalUrl;
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // The backend now returns structured detail objects for every
      // billing error. Older deployments might still send a string —
      // handle both.
      const structured = (detail && typeof detail === "object") ? detail : null;
      if (status === 401) {
        // Session likely expired between mount and request — keep
        // the intent in storage and bounce back through login.
        navigate("/login?next=/billing/continue-checkout", { replace: true });
        return;
      }
      setError({
        code: structured?.error_code || (typeof detail === "string" ? detail : "checkout_unavailable"),
        message: structured?.message || null,
        debug: structured?.debug || {
          raw_detail: typeof detail === "string" ? detail : null,
          response_data: e?.response?.data || null,
          network_error: !e?.response,
        },
        http_status: status || null,
      });
      // IMPORTANT: do NOT consume the intent here — the user must be
      // able to retry without losing their plan selection.
    } finally {
      setBusy(false);
    }
  }, [locale, navigate, t]);

  // Auth-readiness gate. We do NOT call the protected endpoint until
  // the AuthContext has finished its initial /auth/me probe. This
  // prevents the bearer/cookie from missing during a cold post-signup
  // mount and causing a spurious 401.
  useEffect(() => {
    if (loading) return; // wait for auth to resolve
    if (!user) {
      // No user — bounce to login (we keep the intent in storage).
      navigate("/login?next=/billing/continue-checkout", { replace: true });
      return;
    }
    // Only fire on the initial settle of (user, loading). Defer one
    // microtask so the synchronous setState calls inside
    // attemptCheckout don't trip the react-hooks/set-state-in-effect
    // lint rule — semantically identical to calling it directly.
    let cancelled = false;
    Promise.resolve().then(() => { if (!cancelled) attemptCheckout(); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user?.user_id]);

  // ---- Rendering -----------------------------------------------------

  const renderPreparing = (
    <div className="flex flex-col items-center gap-3" data-testid="continue-checkout-loading">
      <Loader2 className="w-7 h-7 animate-spin text-muted-foreground" />
      <h1 className="font-display text-xl">{t("billing.continue.preparing_title")}</h1>
      <p className="text-sm text-muted-foreground max-w-prose">
        {t("billing.continue.preparing_body")}
      </p>
      {intent ? (
        <div className="text-[11px] text-muted-foreground font-mono mt-1">
          {intent.plan} · {intent.interval} · {intent.locale || locale || "en"}
        </div>
      ) : null}
    </div>
  );

  const renderReady = resolvedUrl && !error ? (
    <div className="flex flex-col items-center gap-3" data-testid="continue-checkout-ready">
      <h1 className="font-display text-xl">{t("billing.continue.ready_title")}</h1>
      <p className="text-sm text-muted-foreground max-w-prose">
        {t("billing.continue.ready_body")}
      </p>
      <Button
        type="button"
        onClick={() => { window.location.href = resolvedUrl; }}
        data-testid="continue-checkout-cta"
        className="mt-2"
      >
        {t("billing.continue.cta")}
        <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
      </Button>
      <button
        type="button"
        className="text-xs text-muted-foreground underline mt-1 hover:text-foreground"
        onClick={() => navigate("/billing", { replace: true })}
        data-testid="continue-checkout-cancel"
      >
        {t("billing.continue.cancel")}
      </button>
    </div>
  ) : null;

  // Map known error codes to friendly localized copy. The structured
  // `error_code` is always shown verbatim in the diagnostics panel
  // (admins/owners/dev) regardless of which friendly copy is rendered.
  const friendlyMessage = (() => {
    if (!error) return null;
    if (error.code === "checkout_disabled") return t("billing.continue.error_disabled");
    if (error.code === "invalid_plan" || error.code === "invalid_interval") {
      return t("billing.continue.error_generic");
    }
    if (error.code === "no_url_configured") return t("billing.continue.error_disabled");
    return t("billing.continue.error_generic");
  })();

  const renderError = error ? (
    <div className="flex flex-col items-center gap-3" data-testid="continue-checkout-error">
      <ShieldAlert className="w-7 h-7 text-amber-600 dark:text-amber-400" />
      <h1 className="font-display text-xl">{t("billing.continue.error_title")}</h1>
      <p className="text-sm text-muted-foreground max-w-prose">
        {friendlyMessage}
      </p>
      <div className="flex flex-wrap items-center gap-2 mt-1">
        <Button
          type="button"
          onClick={attemptCheckout}
          disabled={busy}
          data-testid="continue-checkout-retry"
        >
          {busy ? (
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          ) : (
            <RefreshCcw className="w-3.5 h-3.5 mr-1.5" />
          )}
          {t("billing.continue.retry") || "Retry"}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            // The user explicitly chose to abandon — safe to clear
            // the intent now so the same attempt doesn't replay.
            clearCheckoutIntent();
            navigate("/billing", { replace: true });
          }}
          data-testid="continue-checkout-back"
        >
          {t("billing.continue.back_to_billing")}
        </Button>
      </div>

      {showDiagnostics ? (
        <div
          className="mt-4 w-full text-left rounded-lg border border-amber-500/40 bg-amber-500/5 p-3"
          data-testid="continue-checkout-diagnostics"
        >
          <div className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-300 font-semibold">
            Diagnostics (visible to admin / owner / dev only)
          </div>
          <div className="mt-1.5 text-xs text-foreground/90 font-mono break-all">
            <span className="text-muted-foreground">error_code:</span>{" "}
            <span data-testid="continue-checkout-diag-error-code">{String(error.code)}</span>
          </div>
          {error.http_status ? (
            <div className="text-xs text-foreground/90 font-mono">
              <span className="text-muted-foreground">http_status:</span>{" "}
              <span data-testid="continue-checkout-diag-http">{String(error.http_status)}</span>
            </div>
          ) : null}
          {error.message ? (
            <div className="text-xs text-foreground/90 font-mono break-words">
              <span className="text-muted-foreground">message:</span>{" "}
              <span>{String(error.message)}</span>
            </div>
          ) : null}
          {error.debug ? (
            <pre
              className="mt-2 text-[11px] text-foreground/80 bg-card/60 border border-border/60 rounded p-2 overflow-auto max-h-64"
              data-testid="continue-checkout-diag-debug"
            >{JSON.stringify(error.debug, null, 2)}</pre>
          ) : null}
          {intent ? (
            <div className="mt-2 text-[11px] text-muted-foreground font-mono">
              intent.plan={intent.plan} interval={intent.interval} locale={intent.locale || locale || "en"}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  ) : null;

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar showSearch={false} />
      <main
        className="flex-1 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 w-full"
        data-testid="continue-checkout-page"
      >
        <div className="rounded-2xl border border-border/70 bg-card/60 p-6 sm:p-8 text-center">
          {/* Auth gate: while auth context is resolving show the same
              preparing block — never call the backend prematurely. */}
          {loading ? renderPreparing : null}
          {!loading && !resolvedUrl && !error ? renderPreparing : null}
          {renderReady}
          {renderError}
        </div>
      </main>
      <Footer />
    </div>
  );
}
